import axios from "axios";
import { useState, useContext, useEffect, useCallback } from "react";
import { AuthContext } from "../../context/AuthContext";
import { Tag, Table, Modal, Steps, Button, Row, Col, Select, DatePicker } from "antd";
import dayjs from "dayjs";

export default function ViewTicket() {
    const { authData, backUrl } = useContext(AuthContext);

    const [ticketsData, setTicketsData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filters
    const [filterType, setFilterType] = useState(null);
    const [filterPriority, setFilterPriority] = useState(null);
    const [filterDateRange, setFilterDateRange] = useState(null);

    const [modalVisible, setModalVisible] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);

    const stepStatusMap = ["NEW", "IN_PROGRESS", "RESOLVED", "CLOSED"];
    const { Step } = Steps;

    // Fetch tickets with filters
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);

            const params = {
                created_by_id: authData?.employee?.id,
            };

            if (filterType) params.ticket_type = filterType;
            if (filterPriority) params.priority = filterPriority;

            if (filterDateRange?.length === 2) {
                params.start_date = dayjs(filterDateRange[0]).format("YYYY-MM-DD");
                params.end_date = dayjs(filterDateRange[1]).format("YYYY-MM-DD");
            }

            const res = await axios.get(`${backUrl}/api/hr/tickets/`, {
                headers: { Authorization: `Bearer ${authData.access}` },
                params,
            });

            setTicketsData(res.data.results || []);
        } catch (error) {
            console.error("Failed to fetch tickets:", error);
        } finally {
            setLoading(false);
        }
    }, [authData, backUrl, filterType, filterPriority, filterDateRange]);

    // Auto fetch whenever filters change
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const showTicketModal = (ticket) => {
        setSelectedTicket(ticket);
        setModalVisible(true);
    };

    const mainColumns = [
        {
            title: "Subject",
            dataIndex: "subject",
            key: "subject",
            render: (text, record) => (
                <Button type="link" onClick={() => showTicketModal(record)}>
                    {text}
                </Button>
            ),
        },
        {
            title: "Description",
            dataIndex: "description",
            key: "description",
            width: 400,
            render: (text) => <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>{text}</div>,
        },
        {
            title: "Attachment",
            dataIndex: "attachment",
            key: "attachment",
            render: (url) =>
                url ? (
                    <Button type="link" onClick={() => window.open(url, "_blank")} style={{ color: "#0e497b" }}>
                        View
                    </Button>
                ) : (
                    "-"
                ),
        },
        {
            title: "Type",
            dataIndex: "ticket_type",
            key: "ticket_type",
            render: (type) => <Tag color="blue">{type}</Tag>,
        },
        {
            title: "Priority",
            dataIndex: "priority",
            key: "priority",
            render: (p) => {
                let color = "green";
                if (p === "HIGH" || p === "URGENT") color = "red";
                else if (p === "MEDIUM") color = "orange";
                return <Tag color={color}>{p}</Tag>;
            },
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (s) => {
                let color = s === "NEW" ? "blue" : s === "CLOSED" ? "green" : "orange";
                return <Tag color={color}>{s}</Tag>;
            },
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            render: (d) => dayjs(d).format("DD-MM-YYYY HH:mm"),
        },
    ];

    return (
        <>
            <center>
                <h5 className="mb-4">View Tickets</h5>
            </center>

            {/* FILTERS */}
            {/* <Row gutter={16} className="mb-3">
                <Col span={6}>
                    <label><strong>Type</strong></label>
                    <Select
                        placeholder="Select Type"
                        allowClear
                        style={{ width: "100%" }}
                        value={filterType}
                        onChange={(val) => setFilterType(val || null)}
                    >
                        <Select.Option value="SYSTEM">SYSTEM</Select.Option>
                        <Select.Option value="SOFTWARE">SOFTWARE</Select.Option>
                        <Select.Option value="NETWORK">NETWORK</Select.Option>
                        <Select.Option value="OTHER">OTHER</Select.Option>
                    </Select>
                </Col>

                <Col span={6}>
                    <label><strong>Priority</strong></label>
                    <Select
                        placeholder="Select Priority"
                        allowClear
                        style={{ width: "100%" }}
                        value={filterPriority}
                        onChange={(val) => setFilterPriority(val || null)}
                    >
                        <Select.Option value="LOW">LOW</Select.Option>
                        <Select.Option value="MEDIUM">MEDIUM</Select.Option>
                        <Select.Option value="HIGH">HIGH</Select.Option>
                        <Select.Option value="URGENT">URGENT</Select.Option>
                    </Select>
                </Col>

                <Col span={8}>
                    <label><strong>Created At</strong></label>
                    <DatePicker.RangePicker
                        style={{ width: "100%" }}
                        value={filterDateRange}
                        onChange={(val) => setFilterDateRange(val || null)}
                        format="DD-MM-YYYY"
                    />
                </Col>
            </Row> */}

            {/* Table */}
            <Table
                columns={mainColumns}
                dataSource={ticketsData}
                rowKey={(rec) => rec.id}
                loading={loading}
                pagination={{ pageSize: 10 }}
                bordered
                components={{
                    header: {
                        cell: (props) => (
                            <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>
                                {props.children}
                            </th>
                        ),
                    },
                }}
            />

            {/* Modal */}
            <Modal
                title={selectedTicket?.subject || "Ticket Progress"}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
            >
                {selectedTicket && (
                    <Steps current={stepStatusMap.indexOf(selectedTicket.status)} direction="vertical">
                        {stepStatusMap.map((st) => (
                            <Step
                                key={st}
                                title={st}
                                description={`Status: ${st === selectedTicket.status ? "Current" : "Pending"}`}
                            />
                        ))}
                    </Steps>
                )}
            </Modal>
        </>
    );
}
